import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Button, Typography, Card, Grid, Chip, Stack, Container, Skeleton, useTheme, useMediaQuery, Grid2 } from '@mui/material';
import Masonry from '@mui/lab/Masonry';
import { ChevronRight, ChevronLeft, CheckCircle, Check } from '@mui/icons-material';
import { useDashboardFooter } from '../../contexts/DashboardFooterContext';
import { useGetDesignerFileCards } from '../../hooks/use-get-designer-file-cards';
import { projectCategoryLabels, projectStyleLabels } from '../../utils';

interface StyleImage {
  id: string;
  style: string;
  imageIndex: number;
  src: string;
  candidates?: string[];
  width: number;
  height: number;
  alt: string;
}

export interface StyleQuizProps {
  onComplete?: (result: QuizResult) => void;
  onBack?: () => void;
  initialStyleValue?: string | null;
  initialStyleValues?: string[] | null;
  initialFavoriteLook?: number | null;
  initialStyleDetail?: StyleQuizDetail | null;
  beginLabel?: string;
}

export interface StyleImageSelection {
  style?: string | null;
  imageIndex?: number | null;
  src?: string | null;
}

export interface StyleMatchupResult {
  style1?: string | null;
  style2?: string | null;
  winner?: string | null;
}

export interface StyleQuizDetail {
  selectedStyles?: string[] | null;
  selectedImages?: StyleImageSelection[] | null;
  matchupResults?: StyleMatchupResult[] | null;
  finalSelections?: StyleImageSelection[] | null;
}

export interface QuizResult extends StyleQuizDetail {
  type: 'single' | 'combined';
  style: string;
  style1?: string;
  style2?: string;
  description: string;
}

interface Matchup {
  style1: string;
  style2: string;
  image1: StyleImage;
  image2: StyleImage;
  winner?: string;
}

const styles = [
  'Art Deco', 'Bohemian', 'Coastal', 'Contemporary', 'Eclectic',
  'Farmhouse', 'French Country', 'Hollywood Regency', 'Industrial',
  'Maximalism', 'MidCentury', 'Minimalist', 'Modern', 'Modern Farmhouse',
  'Rustic', 'Scandinavian', 'Shabby Chic', 'Traditional', 'Transitional'
];

const PROJECT_STYLE_CARD_PAGE_SIZE = 500;
const STYLE_GRID_INITIAL_COUNT = 20;
const STYLE_GRID_BATCH_COUNT = 16;

const createStyleRecord = <T,>(factory: () => T): Record<string, T> =>
  styles.reduce((acc, styleName) => {
    acc[styleName] = factory();
    return acc;
  }, {} as Record<string, T>);

const getStyleImageIdentity = (
  image: Pick<StyleImage, 'style' | 'src' | 'imageIndex'>
) => `${image.style}::${image.src || image.imageIndex || ''}`;

const hashString = (value: string) => {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

const shuffleImages = (images: StyleImage[], seed: string) =>
  [...images].sort((left, right) => {
    const leftHash = hashString(
      `${seed}::${left.id}::${left.style}::${left.src}::${left.imageIndex}`
    );
    const rightHash = hashString(
      `${seed}::${right.id}::${right.style}::${right.src}::${right.imageIndex}`
    );

    if (leftHash !== rightHash) {
      return leftHash - rightHash;
    }

    return left.id.localeCompare(right.id);
  });

const useProgressiveReveal = ({
  enabled,
  observerRoot,
  resetKey,
  totalCount,
}: {
  enabled: boolean;
  observerRoot: HTMLElement | null;
  resetKey: string;
  totalCount: number;
}) => {
  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(STYLE_GRID_INITIAL_COUNT, totalCount)
  );
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setVisibleCount(Math.min(STYLE_GRID_INITIAL_COUNT, totalCount));
  }, [resetKey, totalCount]);

  const canLoadMore = enabled && visibleCount < totalCount;

  const revealMore = useCallback(() => {
    setVisibleCount((previous) =>
      Math.min(previous + STYLE_GRID_BATCH_COUNT, totalCount)
    );
  }, [totalCount]);

  useEffect(() => {
    if (!enabled || !canLoadMore) return;

    const target = loadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          revealMore();
        }
      },
      {
        root: observerRoot,
        rootMargin: "400px 0px",
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [canLoadMore, enabled, observerRoot, revealMore]);

  return {
    canLoadMore,
    loadMoreRef,
    setVisibleCount,
    visibleCount,
  };
};

const styleDescriptions = {
  'Art Deco': 'Glamorous 1920s style with geometric patterns and luxury materials',
  'Bohemian': 'Free-spirited, eclectic style rich in color and global inspiration',
  'Coastal': 'Beach-inspired design with light colors and natural textures',
  'Contemporary': 'Current style with clean lines and neutral palettes',
  'Eclectic': 'Curated mix of styles, eras, and colors reflecting personal taste',
  'Farmhouse': 'Rustic charm with vintage elements and cozy comfort',
  'French Country': 'Elegant rural French aesthetic with soft colors and antiques',
  'Hollywood Regency': 'Glamorous, dramatic style with bold colors and luxe finishes',
  'Industrial': 'Urban loft aesthetic with raw materials and metal accents',
  'Maximalism': 'Bold, more-is-more approach with rich patterns and colors',
  'MidCentury': 'Retro-futuristic 1950s-60s style with organic curves',
  'Minimalist': 'Pared-down aesthetic centered on simplicity and function',
  'Modern': 'Mid-20th-century design with streamlined forms',
  'Modern Farmhouse': 'Rustic charm meets contemporary comfort with clean lines',
  'Rustic': 'Nature-inspired with raw woods, stone, and cozy textures',
  'Scandinavian': 'Minimal Nordic design with light colors and natural materials',
  'Shabby Chic': 'Vintage romantic style with distressed furniture and soft colors',
  'Traditional': 'Classic, timeless design with rich fabrics and wood tones',
  'Transitional': 'Balanced blend of traditional and modern elements'
};

const hybridStyleDescriptions: { [key: string]: string } = {
  "Bohemian + Scandinavian": "A unique fusion of bohemian and scandinavian styles: A free-spirited, eclectic style rich in color, pattern, and globally inspired décor and minimal and functional with a focus on light, natural materials and clean lines. Together, they create a harmonious blend of both aesthetics.",
  "Bohemian + Minimalist": "A unique fusion of bohemian and minimalist styles: A free-spirited, eclectic style rich in color, pattern, and globally inspired décor and a pared-down aesthetic centered on simplicity, space, and essential function. Together, they create a harmonious blend of both aesthetics.",
  "Contemporary + Traditional": "A unique fusion of contemporary and traditional styles: Current and evolving style that embraces clean lines, neutral palettes, and subtle sophistication and rooted in classic european decor with rich colors, detailed woodwork, and symmetry. Together, they create a harmonious blend of both aesthetics.",
  "Industrial + Modern": "A unique fusion of industrial and modern styles: Urban loft aesthetic with raw materials and metal accents meets mid-20th-century design with streamlined forms and minimal ornamentation. Together, they create a harmonious blend of both aesthetics.",
  // Add more combinations as needed
};

const StyleQuiz: React.FC<StyleQuizProps> = ({
  onComplete,
  onBack,
  initialStyleValue,
  initialStyleValues,
  initialFavoriteLook,
  initialStyleDetail,
  beginLabel = "Begin",
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [step1SelectedImageIds, setStep1SelectedImageIds] = useState<string[]>([]);
  const [selectedImages, setSelectedImages] = useState<StyleImage[]>([]);
  const [competingStyles, setCompetingStyles] = useState<string[]>([]);
  const [matchupResults, setMatchupResults] = useState<Matchup[]>([]);
  const [finalStyles, setFinalStyles] = useState<string[]>([]);
  const [finalSelections, setFinalSelections] = useState<StyleImage[]>([]);
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const [currentMatchupIndex, setCurrentMatchupIndex] = useState(0);
  const [currentRoundWinner, setCurrentRoundWinner] = useState<string | null>(null);
  const [styleFiles, setStyleFiles] = useState<Record<string, string[]>>({});
  const [projectStyleImages, setProjectStyleImages] = useState<Record<string, StyleImage[]>>(
    () => createStyleRecord<StyleImage[]>(() => [])
  );
  const scrollContainerRef = React.useRef<HTMLElement | null>(null);
  const [observerRoot, setObserverRoot] = useState<HTMLElement | null>(null);
  const hasUserInteractedRef = React.useRef(false);
  const hasPrefilledRef = React.useRef(false);
  const shuffleSeedRef = React.useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const { getProjectFileCards } = useGetDesignerFileCards();

  // Helper function to get hybrid description (defined early for use in useEffect)
  const getHybridDescription = useCallback((style1: string, style2: string): string => {
    const key1 = `${style1} + ${style2}`;
    const key2 = `${style2} + ${style1}`;
    
    if (hybridStyleDescriptions[key1]) {
      return hybridStyleDescriptions[key1];
    } else if (hybridStyleDescriptions[key2]) {
      return hybridStyleDescriptions[key2];
    } else {
      const desc1 = styleDescriptions[style1 as keyof typeof styleDescriptions];
      const desc2 = styleDescriptions[style2 as keyof typeof styleDescriptions];
      return `A unique fusion of ${style1.toLowerCase()} and ${style2.toLowerCase()} styles: ${desc1} meets ${desc2}. Together, they create a harmonious blend that balances both aesthetics perfectly.`;
    }
  }, []);

  // When retaking, allow prefill from a stored style value so users can continue quickly.
  const normalizeStyleValue = useCallback((value: string) => {
    return value.replace(/[^a-zA-Z]/g, '').toLowerCase();
  }, []);

  const resolveStyleName = useCallback((value?: string | null) => {
    if (!value) return null;
    const normalized = normalizeStyleValue(value);
    if (!normalized) return null;
    return (
      styles.find((styleName) => normalizeStyleValue(styleName) === normalized) || null
    );
  }, [normalizeStyleValue]);

  const resolveStoredStyleName = useCallback((value?: string | null) => {
    const resolved = resolveStyleName(value);
    if (resolved) return resolved;
    if (value && styles.includes(value)) return value;
    return null;
  }, [resolveStyleName]);

  const resolveProjectStyleName = useCallback((value?: string | null) => {
    if (!value) return null;

    const directMatch = resolveStoredStyleName(value);
    if (directMatch) return directMatch;

    const labelMatch =
      projectStyleLabels[value as keyof typeof projectStyleLabels] ?? null;

    return resolveStoredStyleName(labelMatch);
  }, [resolveStoredStyleName]);

  const initialResolvedStyles = useMemo(() => {
    const rawValues =
      initialStyleDetail?.selectedStyles && initialStyleDetail.selectedStyles.length
        ? initialStyleDetail.selectedStyles
        : initialStyleValues && initialStyleValues.length
        ? initialStyleValues
        : initialStyleValue
        ? [initialStyleValue]
        : [];

    return rawValues
      .map((value) => resolveStyleName(value))
      .filter((value): value is string => Boolean(value))
      .slice(0, 5);
  }, [initialStyleDetail, initialStyleValue, initialStyleValues, resolveStyleName]);

  useEffect(() => {
    let isActive = true;

    const loadProjectStyleImages = async () => {
      try {
        const grouped = createStyleRecord<StyleImage[]>(() => []);
        const seen = new Set<string>();

        let offset = 0;

        while (isActive) {
          const cards = await getProjectFileCards(
            offset,
            PROJECT_STYLE_CARD_PAGE_SIZE
          );
          if (!isActive) return;
          if (!cards.length) break;

          cards.forEach((card) => {
            const src = card.image?.url ?? card.image?.path ?? '';
            if (!src) return;

            const styleValue = card.projectStyle ?? card.style ?? null;
            const styleName = resolveProjectStyleName(
              styleValue ? String(styleValue) : null
            );
            if (!styleName) return;

            const imageKey = `${styleName}::${src}`;
            if (seen.has(imageKey)) return;
            seen.add(imageKey);

            const styleLabel =
              (styleValue
                ? projectStyleLabels[styleValue as keyof typeof projectStyleLabels]
                : null) ?? styleName;
            const categoryLabel =
              (card.projectCategory
                ? projectCategoryLabels[
                    card.projectCategory as keyof typeof projectCategoryLabels
                  ]
                : null) ?? '';
            const alt = [
              card.image?.title?.trim(),
              card.title?.trim(),
              [styleLabel, categoryLabel].filter(Boolean).join(' '),
            ]
              .filter(Boolean)
              .join(' - ');

            grouped[styleName].push({
              id: card.id || `project-${styleName}-${grouped[styleName].length + 1}`,
              style: styleName,
              imageIndex: grouped[styleName].length + 1,
              src,
              candidates: [src],
              width: 600,
              height: 400,
              alt: alt || `${styleLabel} project image`,
            });
          });

          if (cards.length < PROJECT_STYLE_CARD_PAGE_SIZE) {
            break;
          }

          offset += cards.length;
        }

        setProjectStyleImages(grouped);
      } catch (error) {
        console.error('Error loading project style images:', error);
      }
    };

    loadProjectStyleImages();

    return () => {
      isActive = false;
    };
  }, [getProjectFileCards, resolveProjectStyleName]);

  // Resolve public asset path respecting base url in different deployments
  const getBaseUrl = (): string => {
    // Vite exposes import.meta.env.BASE_URL; CRA exposes process.env.PUBLIC_URL
    // Fallback to '/'
    // @ts-ignore
    const viteBase: string | undefined = typeof import.meta !== 'undefined' ? import.meta.env?.BASE_URL : undefined;
    // @ts-ignore
    const craBase: string | undefined = typeof process !== 'undefined' ? (process as any).env?.PUBLIC_URL : undefined;
    const base = viteBase || craBase || '/';
    return base.endsWith('/') ? base : `${base}/`;
  };

  const toStyleSlug = (styleName: string): string => styleName.toLowerCase().replace(/\s+/g, '-');
  const getStyleThumbnailSrc = (styleName: string): string => `${getBaseUrl()}styles/${toStyleSlug(styleName)}/thumb.jpg`;

  const styleFileCandidates = useMemo(() => {
    const candidates: Record<string, string[]> = {};
    styles.forEach(styleName => {
      const slug = toStyleSlug(styleName);
      // If pre-generated styles.json exists and lists files, use those first
      const discovered = styleFiles[slug];
      if (discovered && discovered.length > 0) {
        candidates[slug] = discovered;
        return;
      }
      
      const base = `${getBaseUrl()}style_images/${slug}/`;
      const words = styleName.split(' ').filter(Boolean);
      const titleSlug = words.map(w => w[0].toUpperCase() + w.slice(1).toLowerCase()).join('-');

      const rooms = ['living-room', 'kitchen', 'home-office', 'bedroom', 'dining-room'];
      const exts = ['webp', 'jpg', 'jpeg', 'png'];

      const styleCandidates: string[] = [];
      // 1.*, prioritize numbered file
      exts.forEach(ext => styleCandidates.push(`${base}1.${ext}`));
      // lowercase slug + common rooms
      rooms.forEach(room => {
        exts.forEach(ext => styleCandidates.push(`${base}${slug}-${room}.${ext}`));
      });
      // TitleCase slug + common rooms
      rooms.forEach(room => {
        exts.forEach(ext => styleCandidates.push(`${base}${titleSlug}-${room}.${ext}`));
        // sometimes bedroom file missed hyphen before bedroom in provided assets
        if (room === 'bedroom') {
          exts.forEach(ext => styleCandidates.push(`${base}${titleSlug}bedroom.${ext}`));
        }
      });
      // Some exports include ' (1)' suffix
      rooms.forEach(room => {
        exts.forEach(ext => styleCandidates.push(`${base}${slug}-${room} (1).${ext}`));
        exts.forEach(ext => styleCandidates.push(`${base}${titleSlug}-${room} (1).${ext}`));
      });

      // De-duplicate while preserving order
      candidates[slug] = Array.from(new Set(styleCandidates));
    });
    return candidates;
  }, [styleFiles]);

  // Attempt to load styles.json produced by the tidy script
  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const res = await fetch(`${getBaseUrl()}style_images/styles.json`, { signal: controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        if (data && Array.isArray(data.styles)) {
          const map: Record<string, string[]> = {};
          data.styles.forEach((s: any) => {
            if (s && s.slug && Array.isArray(s.images)) {
              // Normalize to absolute public paths
              map[s.slug] = s.images.map((p: string) => p.startsWith('/') ? `${getBaseUrl()}${p.replace(/^\//,'')}` : `${getBaseUrl()}${p}`);
            }
          });
          setStyleFiles(map);
        }
      } catch (e) {
        // ignore
      }
    };
    load();
    return () => controller.abort();
  }, []);

  // Cache the scrollable dashboard content element
  useEffect(() => {
    const el = document.querySelector('.dashboard-content') as HTMLElement | null;
    if (el) {
      scrollContainerRef.current = el;
      setObserverRoot(el);
    }
  }, []);

  const ImageWithFallback = React.memo<{ sources: string[]; alt: string; sx?: any }>(
    ({ sources, alt, sx }) => {
      const [currentSrc, setCurrentSrc] = useState<string>('');
      const [index, setIndex] = useState(0);
      const [isLoaded, setIsLoaded] = useState(false);
      const [isExhausted, setIsExhausted] = useState(false);
      
      // Set initial source
      useEffect(() => {
        if (sources.length > 0) {
          setCurrentSrc(sources[0]);
          setIndex(0);
          setIsLoaded(false);
          setIsExhausted(false);
        } else {
          setCurrentSrc('');
          setIndex(0);
          setIsLoaded(false);
          setIsExhausted(true);
        }
      }, [sources]);

      const resolvedHeight = sx?.height || 220;
      const resolvedWidth = sx?.width || '100%';
      const resolvedObjectFit = sx?.objectFit || 'cover';

      const handleLoad = useCallback(() => {
        setIsLoaded(true);
        setIsExhausted(false);
      }, []);
      
      const handleError = useCallback(() => {
        const nextIndex = index + 1;
        if (nextIndex < sources.length) {
          setIndex(nextIndex);
          setCurrentSrc(sources[nextIndex]);
          setIsLoaded(false);
          return;
        }
        setIsExhausted(true);
      }, [index, sources]);
      
      if (!currentSrc) return null;
      
      return (
        <Box
          sx={{
            position: 'relative',
            width: resolvedWidth,
            height: resolvedHeight,
            overflow: 'hidden',
            backgroundColor: '#F3F4F6',
          }}
        >
          {!isLoaded && !isExhausted && (
            <Skeleton
              variant="rectangular"
              animation="wave"
              sx={{
                position: 'absolute',
                inset: 0,
                transform: 'none',
                bgcolor: '#E5E7EB',
              }}
            />
          )}
          {!isExhausted && (
            <img
              src={currentSrc}
              alt={alt}
              onLoad={handleLoad}
              onError={handleError}
              loading="lazy"
              decoding="async"
              style={{
                width: resolvedWidth,
                height: resolvedHeight,
                objectFit: resolvedObjectFit,
                display: 'block',
                visibility: isLoaded ? 'visible' : 'hidden',
                ...sx
              }}
            />
          )}
        </Box>
      );
    }
  );

  // Memoized style card component to prevent re-renders
  const StyleCard = React.memo<{
    imageId: string;
    style: string;
    sources: string[];
    onToggle: (imageId: string, style: string, isSelected: boolean) => void;
    selected?: boolean;
    disabled?: boolean;
    imgHeight: number;
  }>(({ imageId, style, sources, onToggle, selected = false, disabled = false, imgHeight }) => {
    const handleClick = useCallback(() => {
      if (disabled) return;
      onToggle(imageId, style, !selected);
    }, [disabled, imageId, onToggle, selected, style]);

    return (
      <Box>
        <Box
          onClick={handleClick}
          className={selected ? 'selected' : ''}
          sx={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            position: 'relative',
            border: 'none',
            opacity: disabled ? 0.45 : 1,
            transition: 'all 0.2s ease-in-out',
            overflow: 'hidden',
            '&:hover': {
              boxShadow: disabled ? 1 : 4,
              transform: disabled ? 'none' : 'translateY(-2px)',
            },
            '& .selection-check': {
              opacity: 0,
              transition: 'opacity 120ms ease',
            },
            '&.selected .selection-check': {
              opacity: 1,
            },
            '& .selection-overlay': {
              opacity: 0,
              transition: 'opacity 120ms ease',
            },
            '&.selected .selection-overlay': {
              opacity: 0.1,
            },
          }}
        >
          {/* Static image element to leverage browser cache; non-interactive */}
          <ImageWithFallback
            sources={sources}
            alt={`${style} representative`}
            sx={{ height: imgHeight, objectFit: 'cover', width: '100%' }}
          />

          <Box className="selection-check" sx={{ position: 'absolute', top: 8, left: 8, backgroundColor: (theme) => (theme.palette as any).purple.main, borderRadius: 1, px: 0.75, py: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 2 }}>
            <Check sx={{ color: '#fff', fontSize: 18 }} />
          </Box>
          <Box className="selection-overlay" sx={{ position: 'absolute', inset: 0, backgroundColor: (theme) => (theme.palette as any).purple.main }} />
        </Box>
      </Box>
    );
  });

  const generateImages = (styleName: string, count = 3): StyleImage[] => {
    // Deprecated for Step 2/5 where we should prefer discovered files. Keep only for
    // controlled contexts like quick tiebreaker placeholders.
    const images: StyleImage[] = [];
    const slug = toStyleSlug(styleName);
    const base = `${getBaseUrl()}style_images/${slug}/`;
    const exts = ['webp', 'jpg', 'jpeg', 'png'];
    for (let i = 1; i <= count; i++) {
      const candidates = exts.map((ext) => `${base}${i}.${ext}`);
      images.push({
        id: `${slug}_${i}`,
        style: styleName,
        imageIndex: i,
        src: candidates[0],
        width: 600,
        height: 400,
        alt: `${styleName} ${i}`,
      });
    }
    return images;
  };

  // Lightweight runtime discovery of images in a style folder when styles.json is missing
  const prefetchImage = (url: string): Promise<boolean> =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });

  const probeStyleFolder = useCallback(async (styleName: string, maxImages: number = 20): Promise<string[]> => {
    const slug = toStyleSlug(styleName);
    const base = `${getBaseUrl()}style_images/${slug}/`;
    const exts = ['webp', 'jpg', 'jpeg', 'png'];
    const found: string[] = [];
    for (let i = 1; i <= maxImages; i++) {
      let picked: string | undefined;
      for (const ext of exts) {
        const url = `${base}${i}.${ext}`;
        // eslint-disable-next-line no-await-in-loop
        const ok = await prefetchImage(url);
        if (ok) {
          picked = url;
          break;
        }
      }
      if (!picked) break; // contiguous numbering expected
      found.push(picked);
    }
    return found;
  }, []);

  // Validate discovered manifest entries to drop any missing files (e.g., stale 5.webp)
  useEffect(() => {
    const validate = async () => {
      const entries = Object.entries(styleFiles);
      if (entries.length === 0) return;
      const updates: Record<string, string[]> = {};
      for (const [slug, urls] of entries) {
        const okUrls: string[] = [];
        for (const url of urls) {
          // eslint-disable-next-line no-await-in-loop
          const ok = await prefetchImage(url);
          if (ok) okUrls.push(url);
        }
        if (okUrls.length !== urls.length) {
          updates[slug] = okUrls;
        }
      }
      if (Object.keys(updates).length > 0) {
        setStyleFiles(prev => ({ ...prev, ...updates }));
      }
    };
    validate();
    // Only depends on styleFiles; prefetchImage is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleFiles]);

  // When user selects styles and no manifest exists for them, probe dynamically
  useEffect(() => {
    const run = async () => {
      const updates: Record<string, string[]> = {};
      for (const styleName of selectedStyles) {
        const slug = toStyleSlug(styleName);
        if (!styleFiles[slug] || styleFiles[slug].length === 0) {
          // eslint-disable-next-line no-await-in-loop
          const images = await probeStyleFolder(styleName);
          if (images.length > 0) updates[slug] = images;
        }
      }
      if (Object.keys(updates).length > 0) {
        setStyleFiles(prev => ({ ...prev, ...updates }));
      }
    };
    run();
    // Only run when selection changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStyles]);

  const getStaticImagesForStyle = useCallback((styleName: string): StyleImage[] => {
    const slug = toStyleSlug(styleName);
    
    // If we have discovered files from styles.json, use those (actual existing files)
    if (styleFiles[slug] && styleFiles[slug].length > 0) {
      return styleFiles[slug].map((src, index) => ({
        id: `${slug}_actual_${index + 1}`,
        style: styleName,
        imageIndex: index + 1,
        src,
        candidates: [src],
        width: 600,
        height: 400,
        alt: `${styleName} ${index + 1}`,
      }));
    }
    
    // Otherwise, avoid fabricating deep indices; keep a small bounded set so we do not
    // reference nonexistent files like 5.webp when only 1-4 exist.
    const base = `${getBaseUrl()}style_images/${slug}/`;
    const exts = ['webp', 'jpg', 'jpeg', 'png'];
    const images: StyleImage[] = [];
    
    // Only try the first four files to align with our current asset sets
    for (let i = 1; i <= 4; i++) {
      const candidates = exts.map((ext) => `${base}${i}.${ext}`);
      images.push({
        id: `${slug}_${i}`,
        style: styleName,
        imageIndex: i,
        src: candidates[0],
        candidates,
        width: 600,
        height: 400,
        alt: `${styleName} ${i}`,
      });
    }
    
    return images;
  }, [styleFiles]);

  const staticStyleImages = useMemo(() => {
    const grouped = createStyleRecord<StyleImage[]>(() => []);

    styles.forEach((styleName) => {
      grouped[styleName] = getStaticImagesForStyle(styleName);
    });

    return grouped;
  }, [getStaticImagesForStyle]);

  const projectImagesByStyle = useMemo(() => {
    const grouped = createStyleRecord<StyleImage[]>(() => []);

    styles.forEach((styleName) => {
      grouped[styleName] = shuffleImages(
        projectStyleImages[styleName] ?? [],
        `${shuffleSeedRef.current}::project::${styleName}`
      );
    });

    return grouped;
  }, [projectStyleImages]);

  const styleImagesByStyle = useMemo(() => {
    const grouped = createStyleRecord<StyleImage[]>(() => []);

    styles.forEach((styleName) => {
      const projectImages = projectImagesByStyle[styleName] ?? [];

      grouped[styleName] = projectImages.length
        ? projectImages
        : shuffleImages(
            staticStyleImages[styleName] ?? [],
            `${shuffleSeedRef.current}::static::${styleName}`
          );
    });

    return grouped;
  }, [projectImagesByStyle, staticStyleImages]);

  const getAllImagesForStyle = useCallback((styleName: string): StyleImage[] => {
    const pooledImages = styleImagesByStyle[styleName];
    if (pooledImages && pooledImages.length > 0) {
      return pooledImages;
    }

    return getStaticImagesForStyle(styleName);
  }, [getStaticImagesForStyle, styleImagesByStyle]);

  const allStyleSelectionImages = useMemo(() => {
    const allProjectImages = shuffleImages(
      styles.flatMap((styleName) => projectImagesByStyle[styleName] ?? []),
      `${shuffleSeedRef.current}::step1::projects`
    );

    if (allProjectImages.length > 0) {
      return allProjectImages;
    }

    return shuffleImages(
      styles.flatMap((styleName) => staticStyleImages[styleName] ?? []),
      `${shuffleSeedRef.current}::step1::static`
    );
  }, [projectImagesByStyle, staticStyleImages]);

  const getImagesForStyles = useCallback(
    (styleNames: string[], seedContext: string) =>
      shuffleImages(
        styleNames.flatMap((styleName) => getAllImagesForStyle(styleName)),
        `${shuffleSeedRef.current}::${seedContext}::${styleNames.join('|')}`
      ),
    [getAllImagesForStyle]
  );

  const step2Images = useMemo(
    () => getImagesForStyles(selectedStyles, 'step2'),
    [getImagesForStyles, selectedStyles]
  );

  const step5Images = useMemo(
    () => getImagesForStyles(finalStyles, 'step5'),
    [finalStyles, getImagesForStyles]
  );

  const step1Reveal = useProgressiveReveal({
    enabled: currentStep === 1,
    observerRoot,
    resetKey: `step1:${allStyleSelectionImages.length}`,
    totalCount: allStyleSelectionImages.length,
  });

  const step2Reveal = useProgressiveReveal({
    enabled: currentStep === 2,
    observerRoot,
    resetKey: `step2:${selectedStyles.join('|')}:${step2Images.length}`,
    totalCount: step2Images.length,
  });

  const step5Reveal = useProgressiveReveal({
    enabled: currentStep === 5,
    observerRoot,
    resetKey: `step5:${finalStyles.join('|')}:${step5Images.length}`,
    totalCount: step5Images.length,
  });

  const visibleStep1Images = useMemo(
    () => allStyleSelectionImages.slice(0, step1Reveal.visibleCount),
    [allStyleSelectionImages, step1Reveal.visibleCount]
  );

  const visibleStep2Images = useMemo(
    () => step2Images.slice(0, step2Reveal.visibleCount),
    [step2Images, step2Reveal.visibleCount]
  );

  const visibleStep5Images = useMemo(
    () => step5Images.slice(0, step5Reveal.visibleCount),
    [step5Images, step5Reveal.visibleCount]
  );

  const { setVisibleCount: setStep1VisibleCount, visibleCount: step1VisibleCount } =
    step1Reveal;
  const step1HasSelections = step1SelectedImageIds.length > 0;
  const step1SelectedImageIdSet = useMemo(
    () => new Set(step1SelectedImageIds),
    [step1SelectedImageIds]
  );
  const allStyleSelectionImagesById = useMemo(
    () =>
      new Map(allStyleSelectionImages.map((image) => [image.id, image] as const)),
    [allStyleSelectionImages]
  );

  useEffect(() => {
    if (!step1SelectedImageIds.length) return;
    let requiredVisibleCount = 0;

    allStyleSelectionImages.forEach((image, index) => {
      if (step1SelectedImageIdSet.has(image.id)) {
        requiredVisibleCount = Math.max(requiredVisibleCount, index + 1);
      }
    });

    if (requiredVisibleCount > step1VisibleCount) {
      setStep1VisibleCount(requiredVisibleCount);
    }
  }, [
    allStyleSelectionImages,
    setStep1VisibleCount,
    step1VisibleCount,
    step1SelectedImageIds,
    step1SelectedImageIdSet,
  ]);

  const preferredStep1CardByStyle = useMemo(() => {
    const preferred: Record<string, string> = {};
    const storedSelections = initialStyleDetail?.selectedImages ?? [];

    storedSelections.forEach((selection) => {
      const styleName = resolveStoredStyleName(selection?.style ?? null);
      if (!styleName || preferred[styleName]) return;

      const src = selection?.src ? String(selection.src) : '';
      if (!src) return;

      const match = allStyleSelectionImages.find(
        (image) => image.style === styleName && image.src === src
      );

      if (match) {
        preferred[styleName] = match.id;
      }
    });

    return preferred;
  }, [allStyleSelectionImages, initialStyleDetail, resolveStoredStyleName]);

  useEffect(() => {
    if (hasPrefilledRef.current || hasUserInteractedRef.current) {
      return;
    }
    if (!initialResolvedStyles.length || !allStyleSelectionImages.length) {
      return;
    }

    const nextSelectedIds = initialResolvedStyles
      .map((styleName) => {
        const preferredCardId = preferredStep1CardByStyle[styleName];
        if (
          preferredCardId &&
          allStyleSelectionImagesById.has(preferredCardId)
        ) {
          return preferredCardId;
        }

        const firstCard = allStyleSelectionImages.find(
          (image) => image.style === styleName
        );
        return firstCard?.id ?? null;
      })
      .filter((value): value is string => Boolean(value));

    if (!nextSelectedIds.length) return;

    setStep1SelectedImageIds(nextSelectedIds);
    hasPrefilledRef.current = true;
  }, [
    allStyleSelectionImages,
    allStyleSelectionImagesById,
    initialResolvedStyles,
    preferredStep1CardByStyle,
  ]);

  const listStyleFirstCandidates = useCallback((styleName: string): string[] => {
    const primary = getAllImagesForStyle(styleName)[0];
    const primarySources =
      primary?.candidates?.length ? primary.candidates : primary?.src ? [primary.src] : [];
    const fallbackSources = styleFileCandidates[toStyleSlug(styleName)] || [];

    return Array.from(
      new Set([...primarySources, ...fallbackSources].filter(Boolean))
    );
  }, [getAllImagesForStyle, styleFileCandidates]);

  const isSameStyleImage = useCallback(
    (
      left: Pick<StyleImage, 'style' | 'src' | 'imageIndex'>,
      right: Pick<StyleImage, 'style' | 'src' | 'imageIndex'>
    ) => getStyleImageIdentity(left) === getStyleImageIdentity(right),
    []
  );

  const mapStoredSelectionsToImages = useCallback((selections: StyleImageSelection[] | null | undefined, options?: { max?: number; allowedStyles?: Set<string> }) => {
    if (!selections || selections.length === 0) return [];
    const max = options?.max ?? selections.length;
    const allowedStyles = options?.allowedStyles;
    const mapped: StyleImage[] = [];
    const seen = new Set<string>();

    for (const selection of selections) {
      if (mapped.length >= max) break;
      const resolvedStyle = resolveStoredStyleName(selection?.style ?? null);
      if (!resolvedStyle) continue;
      if (allowedStyles && !allowedStyles.has(resolvedStyle)) continue;

      const images = getAllImagesForStyle(resolvedStyle);
      const selectionIndex = selection?.imageIndex ?? null;
      const indexValue = typeof selectionIndex === 'number' ? selectionIndex : selectionIndex ? Number(selectionIndex) : null;
      let match: StyleImage | undefined;

      if (selection?.src) {
        match = images.find((img) => img.src === String(selection.src));
      }

      if (!match && indexValue && Number.isFinite(indexValue)) {
        match = images.find((img) => img.imageIndex === indexValue);
      }

      if (!match && selection?.src) {
        match = {
          id: `${toStyleSlug(resolvedStyle)}_stored_${indexValue ?? 'x'}`,
          style: resolvedStyle,
          imageIndex: indexValue && Number.isFinite(indexValue) ? indexValue : 1,
          src: String(selection.src),
          candidates: [String(selection.src)],
          width: 600,
          height: 400,
          alt: `${resolvedStyle} ${indexValue ?? ''}`.trim(),
        };
      }

      if (!match && images.length > 0) {
        match = images[0];
      }

      if (match) {
        const key = getStyleImageIdentity(match);
        if (!seen.has(key)) {
          seen.add(key);
          mapped.push(match);
        }
      }
    }

    return mapped;
  }, [getAllImagesForStyle, resolveStoredStyleName]);

  // getHybridDescription is now defined earlier as a useCallback

  const handleToggleStyleRef = useCallback((imageId: string, style: string, isSelected: boolean) => {
    hasUserInteractedRef.current = true;
    setStep1SelectedImageIds((previous) => {
      if (isSelected) {
        if (previous.includes(imageId)) return previous;
        if (previous.length >= 5) return previous;
        return [...previous, imageId];
      }

      return previous.filter((id) => id !== imageId);
    });
  }, []);

  const getContinueIndex = (): number | undefined => {
    switch (currentStep) {
      // Quiz logic defines 5 steps (excluding Begin and Results)
      // 1: Initial style selection
      // 2: Image selection
      // 3: Style match check (logic only, no UI)
      // 4: Head-to-head tiebreaker
      // 5: Final style decision
      case 1: return 1;
      case 2: return 2;
      case 4: return 3;
      case 5: return 4;
      case 6: return 4; // Results screen should still show bottom bar (Save and Continue)
      default: return undefined;
    }
  };

  const FixedBottomBar = ({ onBack, onNext, nextDisabled, nextButtonId, label }: { onBack?: () => void; onNext?: () => void; nextDisabled?: boolean; nextButtonId?: string; label?: string; }) => {
    const { setFooter, clearFooter } = useDashboardFooter();

    // Memoize the index calculation
    const index = useMemo(() => getContinueIndex(), [currentStep]);

    // Stabilize callbacks so the effect does not depend on unstable identities
    const backRef = React.useRef<(() => void) | undefined>(onBack);
    const nextRef = React.useRef<(() => void) | undefined>(onNext);
    useEffect(() => { backRef.current = onBack; }, [onBack]);
    useEffect(() => { nextRef.current = onNext; }, [onNext]);

    const handleBack = useCallback(() => { if (backRef.current) backRef.current(); }, []);
    const handleNext = useCallback(() => { if (nextRef.current) nextRef.current(); }, []);
    const hasBack = !!onBack;

    // Memoize the button content to avoid recreating on every render
    const buttonContent = useMemo(() => {
      if (index === undefined) return null;
      
      return (
        <>
          <Button 
            onClick={handleBack} 
            variant="text" 
            startIcon={<ChevronLeft sx={{ width: 16, height: 16 }} />} 
            disabled={!hasBack}
            sx={{ fontSize: { xs: '14px', sm: '16px' } }}
          >
            Back
          </Button>
          <Button 
            id={nextButtonId} 
            onClick={handleNext} 
            disabled={!!nextDisabled} 
            variant="contained" 
            endIcon={<ChevronRight sx={{ width: 16, height: 16 }} />} 
            sx={{ 
              px: 4,
              fontSize: { xs: '14px', sm: '16px' }
            }}
          >
            {label ?? (currentStep === 6 ? 'Save and Continue' : `Continue ${index} of 4`)}
          </Button>
        </>
      );
    }, [index, nextButtonId, nextDisabled, label, currentStep, handleBack, handleNext, hasBack]);

    useEffect(() => {
      if (buttonContent === null) {
        clearFooter();
        return;
      }
      setFooter(buttonContent);
      return () => clearFooter();
    }, [buttonContent, setFooter, clearFooter]);
    
    return null;
  };

  const proceedToStep2 = () => {
    const chosen = Array.from(
      new Set(
        step1SelectedImageIds
          .map((imageId) => allStyleSelectionImagesById.get(imageId)?.style ?? null)
          .filter((styleName): styleName is string => Boolean(styleName))
      )
    );
    if (chosen.length >= 1) {
      setSelectedStyles(chosen);
      const allowedStyles = new Set(chosen);
      setSelectedImages((previous) =>
        previous.filter((image) => allowedStyles.has(image.style))
      );

      if (!hasUserInteractedRef.current && selectedImages.length === 0) {
        const storedSelections = initialStyleDetail?.selectedImages ?? null;
        const preselectedFromStored = mapStoredSelectionsToImages(storedSelections, { max: 3, allowedStyles });

        if (preselectedFromStored.length) {
          setSelectedImages(preselectedFromStored);
        } else if (Number.isFinite(initialFavoriteLook)) {
          const preferredIndex = Math.max(1, Number(initialFavoriteLook));
          const preselected: StyleImage[] = [];
          for (const styleName of chosen) {
            if (preselected.length >= 3) break;
            const images = getAllImagesForStyle(styleName);
            if (!images.length) continue;
            const image = images[preferredIndex - 1] || images[0];
            if (image) preselected.push(image);
          }
          if (preselected.length) {
            setSelectedImages(preselected);
          }
        }
      }

      setCurrentStep(2);
    }
  };

  const handleImageSelection = (image: StyleImage) => {
    hasUserInteractedRef.current = true;
    if (selectedImages.some((img) => isSameStyleImage(img, image))) {
      setSelectedImages(selectedImages.filter((img) => !isSameStyleImage(img, image)));
    } else if (selectedImages.length < 3) {
      setSelectedImages([...selectedImages, image]);
    }
    // Prevent scroll jump to top by preserving current scroll position
    const sc = scrollContainerRef.current;
    if (sc) {
      const y = sc.scrollTop;
      requestAnimationFrame(() => sc.scrollTo({ top: y }));
    }
  };

  const proceedToStep3 = () => {
    if (selectedImages.length >= 1) {
      const uniqueStyles = Array.from(new Set(selectedImages.map(img => img.style)));
      
      if (uniqueStyles.length === 1) {
        // Step 3: Style match check — all picked images are same style; skip to results
        setQuizResult({ 
          type: 'single', 
          style: uniqueStyles[0],
          description: styleDescriptions[uniqueStyles[0] as keyof typeof styleDescriptions]
        });
        setCurrentStep(6);
      } else {
        // Step 4: Head-to-head tiebreaker (Round 1 of n) per quiz logic
        setCompetingStyles(uniqueStyles);
        setCurrentMatchupIndex(0);
        setCurrentStep(4);
      }
    }
  };

  const generateMatchups = (styles: string[]): Matchup[] => {
    const matchups: Matchup[] = [];
    for (let i = 0; i < styles.length; i++) {
      for (let j = i + 1; j < styles.length; j++) {
        matchups.push({
          style1: styles[i],
          style2: styles[j],
          image1: generateImages(styles[i], 1)[0],
          image2: generateImages(styles[j], 1)[0]
        });
      }
    }
    return matchups;
  };

  const prefilledMatchupWinnerMap = useMemo(() => {
    const results = initialStyleDetail?.matchupResults;
    if (!results || results.length === 0) return new Map<string, string>();
    const map = new Map<string, string>();
    results.forEach((result) => {
      const style1 = resolveStoredStyleName(result?.style1 ?? null);
      const style2 = resolveStoredStyleName(result?.style2 ?? null);
      const winner = resolveStoredStyleName(result?.winner ?? null);
      if (!style1 || !style2 || !winner) return;
      const key = [style1, style2].sort().join('|');
      map.set(key, winner);
    });
    return map;
  }, [initialStyleDetail, resolveStoredStyleName]);

  const getPrefilledWinnerForMatchup = useCallback((matchup: Matchup) => {
    if (!prefilledMatchupWinnerMap.size) return null;
    const key = [matchup.style1, matchup.style2].sort().join('|');
    return prefilledMatchupWinnerMap.get(key) || null;
  }, [prefilledMatchupWinnerMap]);

  const commitCurrentMatchup = (matchup: Matchup) => {
    if (!currentRoundWinner) return;
    const newResults = [...matchupResults, { ...matchup, winner: currentRoundWinner }];
    setMatchupResults(newResults);
    const matchups = generateMatchups(competingStyles);
    if (newResults.length >= matchups.length) {
      const scores: { [key: string]: number } = {};
      competingStyles.forEach(style => (scores[style] = 0));
      newResults.forEach(result => result.winner && scores[result.winner]++);
      const top2 = Object.entries(scores)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 2)
        .map(([style]) => style);
      setFinalStyles(top2);
      setCurrentStep(5);
      setCurrentRoundWinner(null);
    } else {
      setCurrentMatchupIndex(prev => prev + 1);
      setCurrentRoundWinner(null);
    }
  };

  const handleMatchupBack = () => {
    if (currentMatchupIndex > 0) {
      const lastResult = matchupResults[matchupResults.length - 1];
      setMatchupResults((prev) => prev.slice(0, -1));
      setCurrentMatchupIndex((prev) => Math.max(prev - 1, 0));
      setCurrentRoundWinner(lastResult?.winner ?? null);
      return;
    }

    setCurrentStep(2);
    setCurrentRoundWinner(null);
    setMatchupResults([]);
    setCurrentMatchupIndex(0);
    setFinalStyles([]);
    setFinalSelections([]);
    setCompetingStyles([]);
  };

  const handleFinalBack = () => {
    const matchups = generateMatchups(competingStyles);
    if (matchups.length === 0) {
      setCurrentStep(2);
      setCurrentRoundWinner(null);
      setMatchupResults([]);
      setCurrentMatchupIndex(0);
      setFinalStyles([]);
      setFinalSelections([]);
      setCompetingStyles([]);
      return;
    }

    const lastResult = matchupResults[matchupResults.length - 1];
    setFinalSelections([]);
    setFinalStyles([]);
    setCurrentStep(4);
    setCurrentMatchupIndex(Math.max(matchups.length - 1, 0));
    setMatchupResults((prev) => prev.slice(0, -1));
    setCurrentRoundWinner(lastResult?.winner ?? null);
  };

  useEffect(() => {
    if (currentStep !== 4) return;
    if (hasUserInteractedRef.current) return;
    if (currentRoundWinner) return;
    const matchups = generateMatchups(competingStyles);
    const currentMatchup = matchups[currentMatchupIndex];
    if (!currentMatchup) return;
    const winner = getPrefilledWinnerForMatchup(currentMatchup);
    if (winner) {
      setCurrentRoundWinner(winner);
    }
  }, [currentStep, currentMatchupIndex, competingStyles, currentRoundWinner, getPrefilledWinnerForMatchup]);

  const handleFinalSelection = (image: StyleImage) => {
    hasUserInteractedRef.current = true;
    if (finalSelections.some((img) => isSameStyleImage(img, image))) {
      setFinalSelections(finalSelections.filter((img) => !isSameStyleImage(img, image)));
    } else if (finalSelections.length < 4) {
      setFinalSelections([...finalSelections, image]);
    }
  };

  // --- Final step helpers ----------------------------------------------------

  const MIN_FINAL_PICKS = 1;    // user can advance with at least 1 image
  const MAX_FINAL_PICKS = 4;    // UI lets them choose up to 4

  /** Count how many images were chosen for each style. */
  function tallySelectionsByStyle(selections: StyleImage[]): Record<string, number> {
    return selections.reduce<Record<string, number>>((counts, img) => {
      counts[img.style] = (counts[img.style] ?? 0) + 1;
      return counts;
    }, {});
  }

  /** Build a single-style result object. */
  function makeSingleStyleResult(style: string): QuizResult {
    return {
      type: 'single',
      style,
      description: styleDescriptions[style as keyof typeof styleDescriptions],
    };
  }

  /** Build a hybrid result object for a tie between two finalist styles. */
  const makeHybridResult = useCallback((a: string, b: string): QuizResult => {
    return {
      type: 'combined',
      style: `${a} + ${b}`,
      style1: a,
      style2: b,
      description: getHybridDescription(a, b),
    };
  }, [getHybridDescription]);

  // --- Final step action -----------------------------------------------------

  /**
   * Decide the user's style from their 1–4 final picks.
   * - If all picks are from one style → single style.
   * - If picks include both finalists → whichever has more wins.
   * - If tied → hybrid of the two finalists.
   */
  const completeFinalStep = () => {
    if (finalSelections.length < MIN_FINAL_PICKS) return;

    // Defensive: ensure we have two finalists in a stable order.
    const [finalistA, finalistB] = finalStyles;
    if (!finalistA || !finalistB) return;

    const counts = tallySelectionsByStyle(finalSelections);
    const stylesPicked = Object.keys(counts);

    let result: QuizResult;

    // Case 1: user only picked from a single style
    if (stylesPicked.length === 1) {
      result = makeSingleStyleResult(stylesPicked[0]);
    } else {
      // Case 2: user picked from both finalists — compare counts
      const aCount = counts[finalistA] ?? 0;
      const bCount = counts[finalistB] ?? 0;

      if (aCount > bCount) {
        result = makeSingleStyleResult(finalistA);
      } else if (bCount > aCount) {
        result = makeSingleStyleResult(finalistB);
      } else {
        // Case 3: tie → hybrid
        result = makeHybridResult(finalistA, finalistB);
      }
    }

    setQuizResult(result);
    setCurrentStep(6);
  };

  const handleResultsBack = () => {
    hasUserInteractedRef.current = true;
    setQuizResult(null);

    if (finalStyles.length > 0) {
      setCurrentStep(5);
      return;
    }

    if (competingStyles.length > 0) {
      setCurrentStep(4);
      return;
    }

    if (selectedImages.length > 0) {
      setCurrentStep(2);
      return;
    }

    setCurrentStep(1);
  };

  useEffect(() => {
    if (currentStep !== 5) return;
    if (hasUserInteractedRef.current) return;
    if (finalSelections.length > 0) return;

    const allowedStyles = new Set(finalStyles);
    const storedFinalSelections = initialStyleDetail?.finalSelections ?? null;
    const prefilledFinals = mapStoredSelectionsToImages(storedFinalSelections, {
      max: 4,
      allowedStyles,
    });

    if (prefilledFinals.length) {
      setFinalSelections(prefilledFinals);
    }
  }, [currentStep, finalSelections.length, finalStyles, initialStyleDetail, mapStoredSelectionsToImages]);

  const resetQuiz = () => {
    hasUserInteractedRef.current = true;
    setCurrentStep(1);
    setSelectedStyles([]);
    setStep1SelectedImageIds([]);
    setSelectedImages([]);
    setCompetingStyles([]);
    setMatchupResults([]);
    setFinalStyles([]);
    setFinalSelections([]);
    setQuizResult(null);
    setCurrentMatchupIndex(0);
  };

  const quizDetail = useMemo<StyleQuizDetail>(() => {
    const selectedImageDetails = selectedImages.map((image) => ({
      style: image.style,
      imageIndex: image.imageIndex,
      src: image.src,
    }));
    const matchupDetails = matchupResults.map((result) => ({
      style1: result.style1,
      style2: result.style2,
      winner: result.winner ?? null,
    }));
    const finalSelectionDetails = finalSelections.map((image) => ({
      style: image.style,
      imageIndex: image.imageIndex,
      src: image.src,
    }));

    return {
      selectedStyles: selectedStyles.length ? selectedStyles : null,
      selectedImages: selectedImageDetails.length ? selectedImageDetails : null,
      matchupResults: matchupDetails.length ? matchupDetails : null,
      finalSelections: finalSelectionDetails.length ? finalSelectionDetails : null,
    };
  }, [selectedStyles, selectedImages, matchupResults, finalSelections]);

  const completedPayload = useMemo<QuizResult | null>(() => {
    if (!quizResult) return null;
    return { ...quizResult, ...quizDetail };
  }, [quizDetail, quizResult]);

  const resultVisualSelections = useMemo(() => {
    if (!quizResult) return [];

    const allowedStyles = new Set(
      quizResult.type === 'single'
        ? [quizResult.style]
        : [quizResult.style1, quizResult.style2].filter(
            (value): value is string => Boolean(value)
          )
    );
    const sourceSelections = finalSelections.length > 0 ? finalSelections : selectedImages;
    const dedupedSelections: StyleImage[] = [];
    const seen = new Set<string>();

    sourceSelections.forEach((image) => {
      if (!allowedStyles.has(image.style)) return;
      const key = getStyleImageIdentity(image);
      if (seen.has(key)) return;
      seen.add(key);
      dedupedSelections.push(image);
    });

    return dedupedSelections;
  }, [finalSelections, quizResult, selectedImages]);

  const StepHeader = ({ title, subtitle, progress }: { title: string; subtitle: React.ReactNode; progress?: string }) => (
    <Box textAlign="center" mb={4}>
      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        {subtitle}
      </Typography>
      {progress && (
        <Chip 
          label={progress} 
          size="small" 
          sx={{ mt: 2 }}
          color="primary"
        />
      )}
    </Box>
  );

  const heights = [250, 300, 200, 350];
  
  const getStableHeightForId = (id: string): number => {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
    }
    const idx = hash % heights.length;
    return heights[idx];
  };
  
  const ImageGrid = ({ images, onImageSelect, selectedImages, maxSelection }: {
    images: StyleImage[];
    onImageSelect: (image: StyleImage) => void;
    selectedImages: StyleImage[];
    maxSelection?: number;
  }) => (
    <Masonry columns={{ xs: 2, sm: 2, md: 3, lg: 4 }} spacing={2} defaultColumns={4} defaultSpacing={2} defaultHeight={900}>
      {images.map((image) => {
        const isSelected = selectedImages.some((img) => isSameStyleImage(img, image));
        const isDisabled = maxSelection && selectedImages.length >= maxSelection && !isSelected;
        const height = getStableHeightForId(image.id);

        return (
          <Box key={image.id}>
            <Box
              onClick={() => !isDisabled && onImageSelect(image)}
              sx={{
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                position: 'relative',
                border: 'none',
                opacity: isDisabled ? 0.6 : 1,
                transition: 'all 0.2s ease-in-out',
                overflow: 'hidden',
                '&:hover': {
                  transform: isDisabled ? 'none' : 'translateY(-4px)',
                  boxShadow: isDisabled ? 1 : 8,
                },
              }}
            >
              <ImageWithFallback
                sources={image.candidates || [image.src]}
                alt={image.alt}
                sx={{ height: height, objectFit: 'cover' as const, width: '100%' }}
              />

              {/* Style name overlay removed per requirement */}

              {isSelected && (
                <Box
                  className="selection-check"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    backgroundColor: (theme) => (theme.palette as any).purple.main,
                    borderRadius: 1,
                    px: 0.75,
                    py: 0.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 2,
                  }}
                >
                  <Check sx={{ color: '#fff', fontSize: 18 }} />
                </Box>
              )}

              {isSelected && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: (theme) => (theme.palette as any).purple.main,
                    opacity: 0.1,
                  }}
                />
              )}
            </Box>
          </Box>
        );
      })}
    </Masonry>
  );

  const renderIntro = () => (
    <Container maxWidth="lg">
      <StepHeader 
        title="What's your style?"
        subtitle={
          <>
            Are you drawn to a certain design? Which pieces speak to you? Take our easy
            <br />
            style quiz, learn the names to your favorite looks, and we'll show you the
            <br />
            pieces to pull them together.
          </>
        }
      />

      <Box textAlign="center">
        <Button
          onClick={() => setCurrentStep(1)}
          variant="outlined"
          size="large"
          color="secondary"
          endIcon={<ChevronRight sx={{ width: 16, height: 16 }} />}
        >
          {beginLabel}
        </Button>
      </Box>
      <FixedBottomBar 
        onBack={onBack} 
        onNext={() => setCurrentStep(1)} 
        nextDisabled={false} 
        label={beginLabel}
      />
    </Container>
  );

  const renderStep1 = () => (
    <Container maxWidth="xl">
      <StepHeader 
        title="Which style is your favorite look?"
        subtitle={<Typography variant="caption" color="text.secondary">(Pick up to 5)</Typography>}
      />

      {(() => {
        const selectedCardCount = step1SelectedImageIds.length;

        return (
      <Masonry columns={{ xs: 2, sm: 2, md: 3, lg: 4 }} spacing={2} defaultColumns={4} defaultSpacing={2} defaultHeight={800}>
        {visibleStep1Images.map((image, idx) => {
          const sources = image.candidates?.length ? image.candidates : [image.src];
          const isInitiallySelected = step1SelectedImageIdSet.has(image.id);
          const isDisabled =
            selectedCardCount >= 5 && !isInitiallySelected;
          // 3 heights cycling to simulate varied photo heights similar to Figma
          const h = [220, 300, 260][idx % 3];
          return (
            <StyleCard
              key={image.id}
              imageId={image.id}
              style={image.style}
              sources={sources}
              onToggle={handleToggleStyleRef}
              selected={isInitiallySelected}
              disabled={isDisabled}
              imgHeight={h}
            />
          );
        })}
      </Masonry>
        );
      })()}
      {step1Reveal.canLoadMore && <Box ref={step1Reveal.loadMoreRef} sx={{ height: 1 }} />}

      <FixedBottomBar
        nextButtonId="style-quiz-next-btn"
        onBack={onBack ?? (() => setCurrentStep(0))}
        onNext={proceedToStep2}
        nextDisabled={!step1HasSelections}
      />
    </Container>
  );

  const renderStep2 = () => {
    return (
      <Container maxWidth="xl">
        <StepHeader 
          title="Which style is your favorite look?"
          subtitle="Pick up to 3"
        />

        <ImageGrid 
          images={visibleStep2Images}
          onImageSelect={handleImageSelection}
          selectedImages={selectedImages}
          maxSelection={3}
        />
        {step2Reveal.canLoadMore && <Box ref={step2Reveal.loadMoreRef} sx={{ height: 1 }} />}

        <FixedBottomBar onBack={() => setCurrentStep(1)} onNext={proceedToStep3} nextDisabled={selectedImages.length === 0} />
      </Container>
    );
  };

  const renderStep4 = () => {
    const matchups = generateMatchups(competingStyles);
    const totalRounds = matchups.length;
    const currentMatchup = matchups[currentMatchupIndex];
    const currentRound = currentMatchupIndex + 1;

    if (!currentMatchup) return null;

    return (
      <Container maxWidth="md">
        <StepHeader 
          title="Which style appeals to you more?"
          subtitle={`Round ${currentRound} of ${totalRounds}`}
        />

        <Grid container spacing={4}>
          {[
            { style: currentMatchup.style1, image: currentMatchup.image1 },
            { style: currentMatchup.style2, image: currentMatchup.image2 }
          ].map(({ style, image }) => {
            const isSelected = currentRoundWinner === style;
            return (
              <Grid item xs={12} md={6} key={style}>
                <Card 
                  onClick={() => {
                    hasUserInteractedRef.current = true;
                    setCurrentRoundWinner(style);
                  }}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 4 },
                    height: '100%',
                  }}
                >
                  <Box sx={{ position: 'relative', height: 250, overflow: 'hidden' }}>
                    <ImageWithFallback
                      sources={listStyleFirstCandidates(style)}
                      alt={`${style} representative`}
                      sx={{ height: '100%', width: '100%', objectFit: 'cover' as const }}
                    />
                    {isSelected && (
                      <>
                        <Box className="selection-check" sx={{ position: 'absolute', top: 8, left: 8, backgroundColor: (theme) => (theme.palette as any).purple.main, borderRadius: 1, px: 0.75, py: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 2 }}>
                          <Check sx={{ color: '#fff', fontSize: 18 }} />
                        </Box>
                        <Box sx={{ position: 'absolute', inset: 0, backgroundColor: (theme) => (theme.palette as any).purple.main, opacity: 0.08 }} />
                      </>
                    )}
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <FixedBottomBar
          onBack={handleMatchupBack}
          onNext={() => commitCurrentMatchup(currentMatchup)}
          nextDisabled={!currentRoundWinner}
        />
      </Container>
    );
  };

  const renderStep5 = () => {
    return (
      <Container maxWidth="xl">
        <StepHeader 
          title="Select up to 4 images from these two finalist styles."
          subtitle={`Select around 4 of ${step5Images.length}`}
        />
        
        <ImageGrid 
          images={visibleStep5Images}
          onImageSelect={handleFinalSelection}
          selectedImages={finalSelections}
          maxSelection={4}
        />
        {step5Reveal.canLoadMore && <Box ref={step5Reveal.loadMoreRef} sx={{ height: 1 }} />}
        
        <FixedBottomBar onBack={handleFinalBack} onNext={completeFinalStep} nextDisabled={finalSelections.length < 1} />
      </Container>
    );
  };

  const renderResults = () => {
    const isSingle = quizResult?.type === 'single';
    const styleA = isSingle ? quizResult?.style : quizResult?.style1;
    const styleB = isSingle ? undefined : quizResult?.style2;

    const selectedImagesA = styleA
      ? resultVisualSelections.filter((image) => image.style === styleA)
      : [];
    const selectedImagesB = styleB
      ? resultVisualSelections.filter((image) => image.style === styleB)
      : [];

    // Fall back to pooled style images only when no quiz selections are available.
    const imagesA =
      styleA && selectedImagesA.length === 0 ? getAllImagesForStyle(styleA) : [];
    const imagesB =
      styleB && selectedImagesB.length === 0 ? getAllImagesForStyle(styleB) : [];

    const fallbackA = styleA ? generateImages(styleA, 3) : [];
    const fallbackB = styleB ? generateImages(styleB, 3) : [];

    const listA =
      selectedImagesA.length > 0
        ? selectedImagesA
        : imagesA.length > 0
        ? imagesA
        : fallbackA;
    const listB =
      selectedImagesB.length > 0
        ? selectedImagesB
        : imagesB.length > 0
        ? imagesB
        : fallbackB;

    const heroA = listA[0];
    const sideA1 = listA[1];
    const sideA2 = listA[2];
    const heroB = listB[0];

    return (
      <Container maxWidth="lg" sx={{ pb: 10 }}>
        <Grid2 container spacing={{ xs: 2, md: 4 }}>
            {/* Left visuals */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              {isSingle && heroA && (
                <Box display="flex" gap={2}>
                  {/* Side stack */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {sideA1 && (
                      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <ImageWithFallback
                          sources={sideA1.candidates || [sideA1.src]}
                          alt={sideA1.alt}
                          sx={{ width: '100%', height: 200, objectFit: 'cover' }}
                        />
                      </Card>
                    )}
                    {sideA2 && (
                      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <ImageWithFallback
                          sources={sideA2.candidates || [sideA2.src]}
                          alt={sideA2.alt}
                          sx={{ width: '100%', height: 200, objectFit: 'cover' }}
                        />
                      </Card>
                    )}
                  </Box>
                  {/* Hero */}
                  <Box sx={{ flex: 2 }}>
                    <Card sx={{ borderRadius: 2, overflow: 'hidden', height: '100%' }}>
                      <ImageWithFallback
                        sources={heroA.candidates || [heroA.src]}
                        alt={heroA.alt}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Card>
                  </Box>
                </Box>
              )}

              {!isSingle && heroA && heroB && (
                <Grid container spacing={2}>
                  {[heroA, heroB].map((img, i) => (
                    <Grid item xs={12} md={6} key={i}>
                      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <ImageWithFallback
                          sources={img.candidates || [img.src]}
                          alt={img.alt}
                          sx={{ width: '100%', height: 340, objectFit: 'cover' }}
                        />
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid2>

            {/* Right content */}
            <Grid2 size={{ xs: 12, md: 6 }} paddingTop={{ xs: 4, md: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Typography 
                  variant="overline" 
                  color="purple" 
                  fontWeight="bold"
                  letterSpacing={1}
                  sx={{ mb: 1, display: 'block' }}
                >
                  YOUR STYLE IS
                </Typography>
                
                <Typography 
                  variant="h3" 
                  fontWeight="bold"
                  color="#D22268"
                  sx={{ mb: 3, lineHeight: 1.2 }}
                >
                  {quizResult?.style}
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    mb: 4,
                    lineHeight: 1.6,
                    maxWidth: 520,
                    mx: 'auto',
                  }}
                >
                  {quizResult?.description}
                </Typography>


              </Box>
            </Grid2>
          </Grid2>
        <FixedBottomBar onBack={handleResultsBack} onNext={() => onComplete && completedPayload && onComplete(completedPayload)} nextDisabled={false} label="Save and Continue" />
      </Container>
    );
  };

  return (
    <Box sx={{ pb: 10 }} id="style-quiz-root">
      <Box pt={0} pb={4}>
        {currentStep === 0 && renderIntro()}
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 4 && renderStep4()}
        {currentStep === 5 && renderStep5()}
        {currentStep === 6 && renderResults()}
      </Box>
    </Box>
  );
};

export default StyleQuiz;
